// The review tab of a Saju book's manuscript (specs/011 §4): a scope's text slot by slot — the words a person may edit and
// what it was written from — and 검토 완료, which only a person gives, here: no agent command reviews. A person's edit is
// a draft written from the facts now, through the same refusals as an agent's (narrative-writing.mjs).
import { scopeStale } from './narrative-writing.mjs';

const el = (tag, attrs = {}, children = []) => {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'text') node.textContent = value;
    else if (key.startsWith('on')) node.addEventListener(key.slice(2), value);
    else node.setAttribute(key, String(value));
  }
  node.append(...children); return node;
};
// A slot's name within its scope: series/daeun/6/traits/0/title → traits/0/title; doc/… and a frame reading as they are.
const slotName = address => address.replace(/^series\/(?:daeun|seun)\/\d+\//, '');

/** `commands`: narrative-commands.mjs ({ scopeOf, write, review }); `run` the panel's busy wrapper; `refresh()` after a change. */
export function mountNarrativeReview({ commands, run, refresh }) {
  const note = el('p', { class: 'es-work-hint', 'data-testid': 'component-narrative-note' });
  const slots = el('div', { class: 'es-component-form' });
  let shown = null;
  const target = () => ({ scope: shown.props.scope, componentId: shown.id });
  const save = el('button', { type: 'button', text: '원고 저장', 'data-testid': 'component-narrative-save', onclick: () => run(async () => {
    const text = Object.fromEntries([...slots.querySelectorAll('textarea')].map(area => [area.dataset.address, area.value]));
    await commands.write({ ...target(), text }, { person: true });
    refresh();
  }) });
  const review = el('button', { type: 'button', text: '검토 완료', 'data-testid': 'component-narrative-review', onclick: () => run(async () => {
    await commands.review(target());
    refresh();
  }) });
  const element = el('section', { 'data-testid': 'component-narrative-tab', 'aria-label': '원고' }, [el('h4', { text: '원고' }), note, slots, save, review]);
  element.hidden = true;

  /** Shows a component's scope, or hides the tab for anything else (a page's figures alone are not written). */
  function show(component) {
    shown = component?.type === 'saju.narrative' && component.props.scope.series !== 'book' ? component : null;
    element.hidden = !shown;
    if (!shown) return;
    const group = commands.scopeOf(target()), stale = scopeStale(group);
    slots.replaceChildren(...Object.entries(group.slots).map(([address, text]) => el('label', { class: 'es-work-field' }, [el('span', { text: slotName(address) }),
      Object.assign(el('textarea', { rows: 3, 'data-testid': 'component-narrative-slot', 'data-address': address, 'aria-label': `원고 칸 ${slotName(address)}` }), { value: text })])));
    const pages = [...new Set(group.instances.map(instance => instance.page))];
    note.textContent = [`${group.label} · ${pages.join('·')}쪽 ${group.instances.length}곳`,
      stale ? '지금 명식의 사실과 달라 다시 써야 합니다 — 고쳐 저장하면 지금 사실로 쓴 초안이 됩니다' : '',
      group.generator ? `쓴 이 ${group.generator.agent} · 지침 ${group.generator.guide}` : '',
      group.basis ? `근거 ${group.basis.join(', ')}` : '', group.citations ? `인용 ${group.citations.map(item => item.wiki).join(', ')}` : '',
      group.review.state === 'reviewed' ? `검토 완료 ${group.review.by ?? ''} ${group.review.at ?? ''}`.trim() : ''].filter(Boolean).join(' · ');
    review.disabled = stale || group.review.state === 'reviewed';
    review.title = stale ? '지금 명식의 사실로 쓴 글만 검토를 마칠 수 있습니다.' : group.review.state === 'reviewed' ? '이미 검토를 마쳤습니다.' : '';
  }
  return { element, show };
}
